/**
 * API Service - Client HTTP pulito per Gleem Core
 */

import axios, { AxiosInstance, AxiosRequestConfig } from 'axios'
import { handleSubscriptionGate } from './subscriptionGate'

// In development, usa il proxy Vite (/api → backend)
// In production, il proxy nginx gestisce /api
const API_URL = ''

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
})

// Request interceptor: aggiungi token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor: gestisci errori
let isRedirectingToLogin = false

const RETRY_DELAYS_MS = [800, 1500, 2500] // 3 retry transparent (~5s totali)

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Retry trasparente su network error (es. backend in rebuild, blip rete).
    // - GET/HEAD/OPTIONS: sempre idempotenti
    // - POST /api/auth/login: idempotente di fatto (se la req non arriva al server, ripetere è sicuro)
    // - altri POST/PUT/DELETE: NO retry (potrebbero duplicare effetti)
    const cfg = error.config as (AxiosRequestConfig & { __retryCount?: number; __noRetry?: boolean }) | undefined
    const isNetworkOrTimeout = !error.response || error.code === 'ECONNABORTED'
    const method = (cfg?.method || 'get').toLowerCase()
    const url = (cfg?.url || '')
    const isIdempotent = ['get', 'head', 'options'].includes(method)
    const isLoginPost = method === 'post' && url.includes('/api/auth/login')
    if (cfg && isNetworkOrTimeout && (isIdempotent || isLoginPost) && !cfg.__noRetry) {
      cfg.__retryCount = (cfg.__retryCount || 0)
      if (cfg.__retryCount < RETRY_DELAYS_MS.length) {
        const delay = RETRY_DELAYS_MS[cfg.__retryCount]
        cfg.__retryCount += 1
        await new Promise(r => setTimeout(r, delay))
        return apiClient.request(cfg)
      }
    }

    // Gate abbonamento (punto UNICO: subscriptionGate.ts).
    // Solo il 403 con codice SUBSCRIPTION_EXPIRED porta alla pagina di blocco;
    // ogni altro 403 (permessi, RBAC) resta dov'e' e lo gestisce la pagina.
    if (handleSubscriptionGate(error)) {
      return Promise.reject(error)
    }

    if (error.response?.status === 401 && !isRedirectingToLogin) {
      const isAuthCallback = window.location.pathname.includes('/auth/callback')
      const isAlreadyOnLogin = window.location.pathname === '/login'
      if (!isAuthCallback && !isAlreadyOnLogin) {
        // Token scaduto o non valido — pulisci auth + tenant (dedup multiple 401s)
        isRedirectingToLogin = true
        localStorage.removeItem('token')
        localStorage.removeItem('tenant')
        window.location.href = '/login'
      } else if (isAlreadyOnLogin) {
        // Siamo già su /login — pulisci token stale senza redirect
        localStorage.removeItem('token')
        localStorage.removeItem('tenant')
      }
    }

    // Emit global error event for 500s and network errors
    if (error.response?.status === 500) {
      const errorRef: string | null = error.response?.data?.error_ref || null
      window.dispatchEvent(new CustomEvent('gleem:error', {
        detail: {
          error_ref: errorRef,
          status: 500,
          message: 'Si è verificato un errore interno del server'
        }
      }))
    } else if (!error.response) {
      // Network error
      window.dispatchEvent(new CustomEvent('gleem:error', {
        detail: {
          error_ref: null,
          status: 0,
          message: 'Connessione al server persa. Verifica la tua connessione.'
        }
      }))
    }

    return Promise.reject(error)
  }
)

export default apiClient



